// Capa 1 del plan "una sola conversacion por caso" (caso #20261022620039): que la respuesta salga desde el caso.
// - Al responder desde un buzon personal un correo que es de un caso abierto: «Esto es del caso #N (Grupo). ¿Responder
//   desde el caso?». El boton cambia la cuenta al buzon del grupo y pone [Ticket#N] en el asunto.
// - Si igual sale desde el buzon personal, va copia oculta al buzon del grupo (D60): llega al caso.
// - Un reenvio de un companero de algo que ya es caso: «Esto ya es el caso #N: ábrelo y responde allí».
import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import type { Buzon } from '../api/correo';
import type { BuzonGrupo } from '../api/correoEstado';
import type { Caso } from '../api/casos';
import { useMiembros } from '../data/queries';
import { type CasoDelHilo, casoDelHilo, equipoDe } from '../lib/casoDeHilo';
import { Icon } from '../lib/icons';
import { useBuzonesVisibles } from '../screens/bandeja/useHilos';
import { useCasosSinCerrar } from '../screens/bandeja/useVistas';

export type CasoEnCorreo = {
  coincide: CasoDelHilo<Caso> | null;
  // Grupo del caso y su buzon, si esta persona puede enviar desde el (es miembro del grupo).
  grupo: BuzonGrupo | null;
  cuentaGrupo: Buzon | null;
  equipo: (correo: string) => boolean;
  // Un buzon es personal si su correo es el de alguien del equipo (D38).
  esPersonal: (b: Pick<Buzon, 'correo'> | null | undefined) => boolean;
};

// De que caso abierto es un correo (si lo es) y desde donde se le puede responder. `activo`: falso en un caso abierto
// en pantalla (ya se responde desde el caso).
export function useCasoEnCorreo(o: { hiloId?: string | null; asunto: string; participantes: string[]; activo: boolean }): CasoEnCorreo {
  const casos = useCasosSinCerrar();
  const b = useBuzonesVisibles();
  const miembros = useMiembros();
  const grupos = b.grupos.data;
  const equipo = useMemo(() => equipoDe([...(miembros.data ?? []).map((m) => m.email), ...(grupos ?? []).map((g) => g.correo)]), [miembros.data, grupos]);
  const personales = useMemo(() => new Set((miembros.data ?? []).map((m) => (m.email ?? '').toLowerCase()).filter(Boolean)), [miembros.data]);
  const clave = o.participantes.join(',');
  const coincide = useMemo(
    () => (o.activo && casos.data ? casoDelHilo({ hiloId: o.hiloId, asunto: o.asunto, participantes: o.participantes, casos: casos.data }) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [o.activo, o.hiloId, o.asunto, clave, casos.data],
  );
  const grupo = coincide ? ((grupos ?? []).find((g) => g.nombre === coincide.caso.grupo && g.activo !== false) ?? null) : null;
  const cuentaGrupo = grupo?.cuentaId ? ((b.data ?? []).find((x) => x.id === grupo.cuentaId && !x.fallo) ?? null) : null;
  // Un "grupo" cuyo buzon es personal (p. ej. el grupo Johan) no sirve para que lo vea el resto: se trata como sin buzon.
  const grupoReal = grupo && grupo.correo && !personales.has(grupo.correo.toLowerCase()) ? grupo : null;
  return {
    coincide,
    grupo: grupoReal,
    cuentaGrupo: grupoReal ? cuentaGrupo : null,
    equipo,
    esPersonal: (x) => !!x && personales.has(x.correo.toLowerCase()),
  };
}

const enlaceCaso = (id: string) => `/bandeja/casos?caso=${id}`;

// El aviso sobre la respuesta. `desdeCaso`: ya se cambio a responder desde el caso. `reenvio`: el correo es un reenvio
// de un companero. `cco`: el buzon del grupo que va en copia oculta si se responde desde el personal.
export function AvisoCasoEnCorreo({
  info,
  personal,
  desdeCaso,
  reenvio,
  onDesdeCaso,
  onMiBuzon,
  className = '',
}: {
  info: CasoEnCorreo;
  personal: boolean;
  desdeCaso: boolean;
  reenvio: boolean;
  onDesdeCaso?: () => void;
  onMiBuzon?: () => void;
  className?: string;
}) {
  const c = info.coincide?.caso;
  if (!c) return null;
  const numero = `#${c.numero ?? '—'}`;
  const grupo = c.grupo ? ` (${c.grupo})` : '';
  if (desdeCaso && info.cuentaGrupo)
    return (
      <section className={`aviso-caso ok ${className}`} role="status" aria-label="Respuesta desde el caso">
        <Icon name="inbox" size={15} />
        <span>
          <b>Respondes desde el caso {numero}.</b>
          <span className="sb">
            {' '}
            Sale desde {info.cuentaGrupo.correo} con [Ticket#{c.numero}] en el asunto: la respuesta del cliente vuelve al caso.
          </span>
        </span>
        {onMiBuzon ? (
          <div className="acciones">
            <button type="button" className="btn sm ghost" onClick={onMiBuzon}>
              Volver a mi buzón
            </button>
          </div>
        ) : null}
      </section>
    );
  if (!reenvio && !personal) return null;
  const cco = info.grupo?.correo;
  return (
    <section className={`aviso-caso ${className}`} role="note" aria-label={reenvio ? 'Este correo ya es un caso' : 'Este correo es de un caso'}>
      <Icon name="inbox" size={15} />
      <span>
        {reenvio ? (
          <b>
            Esto ya es el caso {numero}
            {grupo}: ábrelo y responde allí.
          </b>
        ) : (
          <b>
            Esto es del caso {numero}
            {grupo}. ¿Responder desde el caso?
          </b>
        )}
        {personal && cco ? <span className="sb"> Si respondes desde tu buzón, va copia oculta a {cco} para que quede en el caso.</span> : null}
      </span>
      <div className="acciones">
        {info.cuentaGrupo && onDesdeCaso ? (
          <button type="button" className={`btn sm ${reenvio ? '' : 'primary'}`} onClick={onDesdeCaso}>
            Responder desde el caso
          </button>
        ) : null}
        <Link className={`btn sm ${reenvio ? 'primary' : 'ghost'}`} to={enlaceCaso(c.id)}>
          Abrir el caso {numero}
        </Link>
      </div>
    </section>
  );
}
